import React from 'react';
import type { HelpCenterStructureNode } from '@/types';
import { collectCategoryIds } from './structureTreeUtils';

interface UseTreeExpandedStateOptions {
  structure: HelpCenterStructureNode[];
  helpCenterId: number;
  expandedCategoryIdsRef?: React.MutableRefObject<Set<string>>;
}

interface UseTreeExpandedStateResult {
  expandedItems: string[];
  setExpandedItems: React.Dispatch<React.SetStateAction<string[]>>;
}

/**
 * Custom hook to manage tree expanded state with localStorage persistence
 */
export function useTreeExpandedState({
  structure,
  helpCenterId,
  expandedCategoryIdsRef,
}: UseTreeExpandedStateOptions): UseTreeExpandedStateResult {
  const storageKey = `help-center-expanded-${helpCenterId}`;

  // Use external ref if provided, otherwise use internal ref
  const internalExpandedRef = React.useRef<Set<string>>(new Set());
  const expandedRef = expandedCategoryIdsRef || internalExpandedRef;

  // Track state
  const hasLoadedFromStorage = React.useRef(false);
  const skipNextSave = React.useRef(false);
  const isInitialMount = React.useRef(true);

  // Load expanded items from localStorage
  const loadExpandedFromStorage = React.useCallback(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        return new Set(JSON.parse(stored) as string[]);
      }
    } catch (err) {
      console.error('Error loading expanded items from localStorage:', err);
    }
    return new Set<string>();
  }, [storageKey]);

  // Initialize expanded items
  const [expandedItems, setExpandedItems] = React.useState<string[]>(() => {
    const allCategoryIds = collectCategoryIds(structure);

    // Try localStorage first
    const storedExpanded = loadExpandedFromStorage();
    if (storedExpanded.size > 0) {
      const validStored = allCategoryIds.filter((id) => storedExpanded.has(id));
      if (validStored.length > 0) {
        hasLoadedFromStorage.current = true;
        return validStored;
      }
    }

    // Fall back to ref if available
    if (expandedRef.current.size > 0) {
      return allCategoryIds.filter((id) => expandedRef.current.has(id));
    }

    // First load - expand all
    return allCategoryIds;
  });

  // Preserve expanded items when structure changes
  React.useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const allCategoryIds = collectCategoryIds(structure);
    const storedExpanded = loadExpandedFromStorage();
    const hasStoredState = storedExpanded.size > 0 && hasLoadedFromStorage.current;

    skipNextSave.current = true;

    setExpandedItems((prev) => {
      let result: string[];

      if (hasStoredState) {
        // Prioritize stored state
        result = allCategoryIds.filter((id) => storedExpanded.has(id));
      } else {
        // Keep existing expanded items that still exist
        const validExisting = prev.filter((id) => allCategoryIds.includes(id));
        const newIds =
          expandedRef.current.size === 0
            ? allCategoryIds.filter((id) => !prev.includes(id))
            : [];
        result = [...validExisting, ...newIds];
      }

      // Update refs
      expandedRef.current = new Set(result);
      if (expandedCategoryIdsRef) {
        expandedCategoryIdsRef.current = new Set(result);
      }

      setTimeout(() => {
        skipNextSave.current = false;
      }, 100);

      return result;
    });
  }, [structure, expandedCategoryIdsRef, loadExpandedFromStorage]);

  // Update ref and localStorage when expandedItems changes
  React.useEffect(() => {
    expandedRef.current = new Set(expandedItems);
    if (expandedCategoryIdsRef) {
      expandedCategoryIdsRef.current = new Set(expandedItems);
    }

    if (skipNextSave.current) {
      return;
    }

    if (hasLoadedFromStorage.current) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(expandedItems));
      } catch (err) {
        console.error('Error saving expanded items to localStorage:', err);
      }
    } else {
      hasLoadedFromStorage.current = true;
    }
  }, [expandedItems, expandedCategoryIdsRef, storageKey]);

  return { expandedItems, setExpandedItems };
}
